import {signOut} from 'aws-amplify/auth'
import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'

import coreApi from '@/api/core.api'
import {authAdapter} from '@/services/auth-adapter'
import {CognitoGroups} from '@/utils/constants'
import {isDevelopment} from '@/utils/env'
import {PermissionsHelper} from '@/utils/permission-helper'

import contacts from './modules/contacts'
import dealerBike from './modules/dealerBike'
import offer from './modules/offer'
import registration from './modules/registration'
import stocks from './modules/stocks'
import ui from './modules/ui'
import usersCredential from './modules/usersCredential'

Vue.use(Vuex)

const debug = process.env.NODE_ENV !== 'production'
const initialState = {
  user: null,
  cognitoUser: null,
  fullUserName: '',
  isPanexUser: false,
  group: null,
  token: null,
  loading: false,
  drawer: false,
  userDrawer: false,
  firebase: null,
  firebaseNews: null,
  dealerMid: null,
  userGroups: [],
  permissions: null,
  version: undefined,
  // Development feature flag
  ...(isDevelopment()
    ? {
        showInactivityTimer: false,
        debugInactivityTimeout: null, // null means use default (2 hours)
      }
    : {}),
}

const state = () => ({...initialState})

const getters = {
  getLoading: state => state.loading,
  getUser: state => state.user,
  getUserGroups: state => state.userGroups,
  getToken: state => state.token,
  getFirebase: state => state.firebase,
  getFirebaseNews: state => state.firebaseNews,
  getGroup: state => state.group,
  getCapabilities: state => state.group?.branchCapabilities,
  getDealerMid: state => state.dealerMid,
  getDealerId: state => state.group.dealerId,
  getDealerCreatedAt: state => state.group.createdAt,
  getDrawerState: state => state.drawer,
  getUserDrawerState: state => state.userDrawer,
  getCognitoUserName: state => state.fullUserName,
  getCognitoUser: state => state.cognitoUser,
  getIdToken: state => state.cognitoUser?.signInUserSession?.idToken?.jwtToken || '',
  isStockUser: state =>
    state.userGroups.includes(CognitoGroups.StockAdmin) || state.userGroups.includes(CognitoGroups.Admin),
  isImportUser: state => state.userGroups.includes(CognitoGroups.Imports),
  isAdmin: state => state.userGroups.includes(CognitoGroups.Admin),
  isDealerAdmin: state =>
    state.userGroups.includes(CognitoGroups.DealerAdmin) || state.userGroups.includes(CognitoGroups.Admin),
  isServicePartner: state => state.userGroups.includes(CognitoGroups.ServicePartner),
  isGarageManager: state =>
    state.userGroups.includes(CognitoGroups.GarageManager) || state.userGroups.includes(CognitoGroups.Admin),
  isSalesAgent: state =>
    state.userGroups.includes(CognitoGroups.SalesAgent) || state.userGroups.includes(CognitoGroups.Admin),
  getPermissions: state => state.permissions,
  getCountry: state => state.group?.country,
  getLocale: state => state.group?.locale.replace('_', '-'),
  getBranch: state => state.group?.branch,
  getAppVersion: state => state.version,
  userHasNoDealer: state => !state.user?.dealerId,
  getIsPanexUser: state => state.isPanexUser,
  // Development feature flag
  ...(isDevelopment()
    ? {
        getShowInactivityTimer: state => state.showInactivityTimer,
        getDebugInactivityTimeout: state => state.debugInactivityTimeout,
      }
    : {}),
}

const actions = {
  setFirebase({commit}, firebase) {
    commit('SET_FIREBASE', firebase)
  },
  setFirebaseNews({commit}, firebase) {
    commit('SET_FIREBASE_NEWS', firebase)
  },
  setLoading({commit}, loading) {
    commit('SET_LOADING', loading)
  },
  setDrawerState({commit}, drawer) {
    commit('SET_DRAWER', drawer)
  },
  setUserDrawerState({commit}, userDrawer) {
    commit('SET_USER_DRAWER', userDrawer)
  },
  async logout({commit}) {
    console.log('🚨 Vuex - logout action called!')
    console.trace('Full call stack:')

    try {
      await signOut()
      commit('RESET_STORE')
      commit('SET_USER_DRAWER', false)
      commit('CLEAR_STORAGE')
    } catch (error) {
      console.error('Error signing out:', error)
      // Even if signOut fails, clear local state
      commit('RESET_STORE')
      commit('SET_USER_DRAWER', false)
      commit('CLEAR_STORAGE')
    }
  },
  async loadDealer({commit, state, dispatch}) {
    // If the user has no dealerId or groupId, skip loading the dealer
    if (!state.user.dealerId && !state.user.groupId) {
      console.warn('User has no dealerId or groupId, skipping loadDealer')
      return
    }

    try {
      const {data: dealer} = await coreApi.getDealer(state.user.dealerId ? state.user.dealerId : state.user.groupId)

      const group = {
        ...dealer,
        dealerId: dealer.id,
      }
      commit('SET_GROUP', group)
      commit('SET_DEALER_MID', group.dealerId)
      await dispatch('calculatePermissions')
    } catch (error) {
      // Do not throw the error to allow the user to access the error page
      console.error('Error loading dealer:', error)
    }
  },
  calculatePermissions: ({commit, state}) => {
    commit('SET_PERMISSIONS', new PermissionsHelper(state.cognitoUser, state.group).calculatePermissions())
  },
  setAppVersion({commit}, version) {
    commit('SET_APP_VERSION', version)
  },
  resetStore({commit}) {
    commit('RESET_STORE')
  },
  /**
   * Refresh the authentication token using authAdapter.
   * Reusable across microfrontends and components.
   * @returns {Promise<string>} The new access token
   */
  async refreshToken({commit}) {
    const session = await authAdapter.getCurrentSession()
    const tokens = session?.tokens
    const newToken = tokens?.accessToken?.toString() || ''
    commit('SET_TOKEN', newToken)
    return newToken
  },
  // Development feature flag
  ...(isDevelopment()
    ? {
        setShowInactivityTimer({commit}, value) {
          commit('SET_SHOW_INACTIVITY_TIMER', value)
        },
        setDebugInactivityTimeout({commit}, value) {
          commit('SET_DEBUG_INACTIVITY_TIMEOUT', value)
        },
      }
    : {}),
}

const mutations = {
  SET_USER(state, user) {
    state.user = user
  },
  SET_COGNITO_USER(state, cognitoUser) {
    state.cognitoUser = cognitoUser
    const attributes = cognitoUser?.attributes || {}
    const fullNameFromGivenFamily = `${attributes.given_name || ''} ${attributes.family_name || ''}`.trim()
    state.fullUserName =
      attributes.displayName ||
      attributes.name ||
      fullNameFromGivenFamily ||
      attributes.preferred_username ||
      attributes.email ||
      ''

    // Set isPanexUser based on presence of panex_id attribute
    state.isPanexUser = !!cognitoUser?.attributes?.panex_id
  },
  SET_USER_GROUPS(state, groups) {
    state.userGroups = groups
  },
  SET_TOKEN(state, token) {
    state.token = token
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_FIREBASE(state, firebase) {
    state.firebase = firebase
  },
  SET_FIREBASE_NEWS(state, firebaseNews) {
    state.firebaseNews = firebaseNews
  },
  SET_DEALER_MID: (state, mid) => {
    state.dealerMid = mid
  },
  SET_GROUP(state, group) {
    state.group = group
  },
  SET_DRAWER(state, drawer) {
    state.drawer = drawer
  },
  SET_USER_DRAWER(state, userDrawer) {
    state.userDrawer = userDrawer
  },
  SET_PERMISSIONS: (state, permissions) => {
    state.permissions = permissions
  },
  SET_APP_VERSION: (state, version) => {
    state.version = version
  },
  // Development feature flag
  ...(isDevelopment()
    ? {
        SET_SHOW_INACTIVITY_TIMER(state, value) {
          state.showInactivityTimer = value
        },
        SET_DEBUG_INACTIVITY_TIMEOUT(state, value) {
          state.debugInactivityTimeout = value
        },
      }
    : {}),
  RESET_STORE(state) {
    Object.keys(initialState).forEach(key => {
      state[key] = initialState[key]
    })
  },
  CLEAR_STORAGE() {
    Object.keys(localStorage).forEach(key => {
      if (!key.startsWith('last-news-')) {
        localStorage.removeItem(key)
      }
    })
    sessionStorage.clear()
  },
}

const dataState = createPersistedState({
  paths: [
    'dealerMid',
    'token',
    'user',
    'drawer',
    'ui.subagentSuggestions',
    'offer.attendedBySuggestions',
    'offer.vehicleModelsOptions',
    'offer.campaignSuggestions',
    'offer.complementSuggestions',
  ],
})

const store = new Vuex.Store({
  modules: {
    registration,
    ui,
    offer,
    stocks,
    usersCredential,
    dealerBike,
    contacts,
  },
  state,
  getters,
  actions,
  mutations,
  strict: debug,
  plugins: [dataState],
})

export default store
